import { useEffect, useId, useRef } from 'react';
import { Spinner } from './ui';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE } from '../lib/ui';

type Props = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'brand' | 'danger';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/** In-app confirmation modal — replaces browser window.confirm. */
export default function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'brand',
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  const titleId = useId();
  const descId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, busy, onCancel]);

  if (!open) return null;

  const danger = tone === 'danger';

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        aria-label="Dismiss"
        className="absolute inset-0 bg-[#2b0018]/80 backdrop-blur-[2px]"
        onClick={() => !busy && onCancel()}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-[0_28px_80px_rgba(43,0,24,0.35)]"
        style={{ border: '1px solid #fce7f3' }}
      >
        <div
          className="px-5 py-4"
          style={{
            background: danger
              ? 'linear-gradient(135deg, #fff1f2 0%, #ffffff 70%)'
              : 'linear-gradient(135deg, #fff5f8 0%, #ffffff 70%)',
            borderBottom: '1px solid #fce7f3',
          }}
        >
          <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${danger ? 'text-[#be123c]' : 'text-[#d6006e]'}`}>
            Please confirm
          </p>
          <h2 id={titleId} className="mt-1 text-lg font-black tracking-tight text-[#6b0030]">
            {title}
          </h2>
        </div>
        <div className="px-5 py-4">
          <p id={descId} className="text-sm leading-relaxed text-[#6b3a52]">
            {description}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-[#fce7f3] bg-[#fffafc] px-5 py-4">
          <button type="button" disabled={busy} onClick={onCancel} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={BTN_PRIMARY}
            style={
              danger
                ? { ...BTN_PRIMARY_STYLE, background: '#be123c', boxShadow: '0 8px 22px rgba(190,18,60,0.28)' }
                : BTN_PRIMARY_STYLE
            }
          >
            {busy && <Spinner />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
